import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const HVAC = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my HVAC business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps HVAC contractors capture 100% of emergency calls 24/7, automate seasonal maintenance reminders, reduce no-shows by 85%, and build recurring service contracts. Rated 4.9/5 by UK HVAC businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for HVAC contractors?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most HVAC contractors recoup the cost within days through emergency call capture and reduced no-shows."
        }
      },
      {
        "@type": "Question",
        "name": "Will it capture emergency heating calls?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. 24/7 emergency call capture ensures you never miss boiler breakdowns or AC failures, even at 2am in winter. Calls are logged instantly with customer details."
        }
      },
      {
        "@type": "Question",
        "name": "Can it automate seasonal maintenance reminders?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Automated reminders for annual boiler servicing and AC maintenance keep your calendar full year-round. HVAC businesses report 200+ customers on automated maintenance schedules."
        }
      },
      {
        "@type": "Question",
        "name": "Does it help build service contracts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Service contract management and automated renewal reminders help build recurring revenue. HVAC contractors report signing 50+ annual maintenance contracts in the first 6 months."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for HVAC Contractors - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "HVAC CRM", url: "/industries/hvac" }
  ];

  const industryData = {
    name: 'HVAC Contractor',
    icon: '❄️',
    title: 'Capture Emergency HVAC Calls & Build Seasonal Maintenance Contracts',
    subtitle: 'Never miss another emergency call. Automated seasonal reminders keep you booked year-round',
    
    challenges: [
      'Missing emergency heating calls when customers need you most',
      'Customers forget their annual boiler service until it breaks',
      'High no-show rates for maintenance appointments',
      'Difficulty building recurring service contract revenue',
      'Seasonal gaps in bookings during mild weather',
      'Lost quotes because you didn\'t follow up fast enough',
      'Can\'t compete with bigger companies\' 24/7 availability',
      'Manually tracking warranty expiration dates'
    ],

    solutions: [
      '24/7 Emergency Call Capture - Never miss a boiler breakdown or AC failure, even at 3am',
      'Automated Seasonal Maintenance Reminders - Keep customers on annual service schedules',
      'SMS Appointment Reminders - Reduce no-shows by 85% and keep your day running smoothly',
      'Service Contract Management - Track and automate annual maintenance contract renewals',
      'Instant Quote Calculator - Send professional quotes in under 3 minutes',
      'Year-Round Booking System - Fill seasonal gaps with proactive maintenance campaigns',
      'Job History Tracking - Instant access to every customer\'s equipment history',
      'Warranty Expiration Alerts - Remind customers before warranties expire'
    ],

    features: [
      {
        title: '24/7 Emergency Call Capture',
        description: 'Automated system captures emergency heating and cooling calls round the clock. Customers get instant confirmation while you receive call details immediately.',
        benefit: 'Capture 40% more emergency jobs'
      },
      {
        title: 'Seasonal Maintenance Automation',
        description: 'Automated reminders for boiler servicing (autumn) and AC checks (spring) keep your schedule full year-round.',
        benefit: '200+ customers on auto-pilot maintenance schedules'
      },
      {
        title: 'Service Contract Management',
        description: 'Track annual service contracts with automated renewal reminders. Build predictable recurring revenue.',
        benefit: 'Sign 50+ annual contracts in 6 months'
      },
      {
        title: 'Instant HVAC Quote Calculator',
        description: 'Pre-built templates for common jobs (boiler replacement, AC installation, system upgrades). Send quotes in under 3 minutes.',
        benefit: 'Convert 75% of quotes to bookings'
      },
      {
        title: 'Job History & Equipment Tracking',
        description: 'Complete history of every job, every boiler serviced, every system installed. Instant access from any device.',
        benefit: 'Professional service that builds trust'
      },
      {
        title: 'Commercial HVAC Client Portal',
        description: 'Property managers submit maintenance requests 24/7 and view service history. Professional system wins commercial contracts.',
        benefit: 'Sign 8+ commercial buildings using this feature'
      }
    ],

    results: [
      '40% more emergency heating calls captured (especially winter)',
      '200+ customers on automated annual service schedules',
      '85% fewer no-shows with automated SMS reminders',
      '50+ annual maintenance contracts signed',
      'Year-round bookings eliminating seasonal revenue gaps',
      '75% quote-to-booking conversion rate',
      'Save 15+ hours per week on admin and scheduling',
      '8+ commercial buildings signed using professional client portal'
    ],

    reviews: [
      {
        name: 'James T.',
        business: 'Gas Safe Engineer, Manchester',
        text: 'Emergency call capture is a game-changer. Used to miss 10-15 calls per week when I was on jobs. Now I capture everything 24/7 and customers get instant confirmation. Booked solid.',
        rating: 5
      },
      {
        name: 'Sarah M.',
        business: 'HVAC Contractor, Birmingham',  
        text: 'The seasonal maintenance reminders keep my calendar full year-round. 200+ customers on automated schedules now. No more slow periods.',
        rating: 5
      },
      {
        name: 'David K.',
        business: 'Heating Engineer, Leeds',
        text: 'Built 50+ annual service contracts in 6 months using the contract management system. This created £15,000 in predictable recurring revenue. Best investment I\'ve made.',
        rating: 5
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for HVAC Contractors UK | Emergency Call Capture ❄️ Rated 4.9/5"
        description="Capture emergency heating/AC calls 24/7. Automate seasonal maintenance, build service contracts, reduce no-shows 85%. Start £5 trial."
        keywords="HVAC CRM UK, heating engineer software, AC contractor CRM, boiler service software UK, HVAC customer management, emergency heating call capture"
        url="/industries/hvac"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default HVAC;
